import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Train, ChevronRight, MapPin, Camera, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { BrandChip } from "@/components/BrandChip";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function States() {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${API}/states`);
        setStates(response.data);
      } catch (error) {
        console.error("Error fetching states:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStates();
  }, []);

  const filteredStates = states.filter(state => 
    state.state.toLowerCase().includes(searchQuery.toLowerCase()) ||
    state.state_code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalDepots = states.reduce((sum, s) => sum + s.depot_count, 0);
  const totalPhotos = states.reduce((sum, s) => sum + s.photo_count, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a] page-transition">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/")}
              className="text-white hover:bg-white/5"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="font-serif font-bold text-lg text-white">Browse by State & Province</h1>
              <p className="text-sm text-white/50">{states.length} states & provinces with depots</p>
            </div>
            <Link to="/" className="flex items-center gap-2">
              <BrandChip size={36} to={null} />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold stat-number">{states.length}</p>
              <p className="text-sm text-white/40 mt-1">States & Provinces</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold text-white">{totalDepots.toLocaleString()}</p>
              <p className="text-sm text-white/40 mt-1">Total Depots</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold text-white">{totalPhotos.toLocaleString()}</p>
              <p className="text-sm text-white/40 mt-1">With Photos</p>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-white/5">
            <CardContent className="p-5 text-center">
              <p className="font-mono text-3xl font-bold text-emerald-400">
                {totalDepots > 0 ? Math.round(totalPhotos / totalDepots * 100) : 0}%
              </p>
              <p className="text-sm text-white/40 mt-1">Coverage</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <Input
            type="text"
            placeholder="Search states..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-amber-500/50"
            data-testid="state-search-input"
          />
        </div>

        {/* States Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array(9).fill(0).map((_, i) => (
              <Card key={i} className="bg-[#111] border-white/5 animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-white/10 rounded w-1/2 mb-4" />
                  <div className="h-4 bg-white/10 rounded w-3/4" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="states-grid">
            {filteredStates.map((state, index) => (
              <Link
                key={state.state_code}
                to={`/states/${state.state_code}`}
                className="block"
                data-testid={`state-card-${state.state_code}`}
              >
                <Card className="depot-card h-full bg-[#111] border-white/5 hover:border-amber-500/30 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h2 className="font-serif text-2xl font-bold text-white">{state.state}</h2>
                        <p className="text-sm text-white/40 font-mono">{state.state_code}</p>
                      </div>
                      <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-amber-500" />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-white/40">
                          <Train className="w-4 h-4" />
                          Depots
                        </span>
                        <span className="font-mono font-bold text-white">{state.depot_count}</span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2 text-white/40">
                          <Camera className="w-4 h-4" />
                          With Photos
                        </span>
                        <span className="font-mono font-bold text-white">{state.photo_count}</span>
                      </div>

                      <div>
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-white/40">Coverage</span>
                          <span className="font-mono text-emerald-400">{Math.round(state.coverage)}%</span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                            style={{ width: `${state.coverage}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-end mt-5 text-amber-500">
                      <span className="text-sm font-medium">View Depots</span>
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {filteredStates.length === 0 && !loading && (
          <div className="text-center py-16">
            <MapPin className="w-16 h-16 mx-auto mb-4 text-white/10" />
            <p className="text-white/40">No states found matching "{searchQuery}"</p>
          </div>
        )}
      </main>
    </div>
  );
}
